import { useEffect, useMemo, useState } from "react";
import type { CSSProperties, ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { renderEmailTokens, type EmailTemplateKey, type EmailTemplateVar } from "@talenthop/shared";
import { api } from "../../lib/api.js";
import { useAuth } from "../../lib/AuthContext.js";
import { Alert, Check, Copy, Edit, Info, Mail, RefreshCw, Search } from "../../components/icons.js";
import { apiErrorText, Switch } from "./adminUi.js";

/**
 * Admin · Email templates — the transactional emails TalentHop sends, editable by an Owner
 * without a deploy (migration 0073).
 *
 * Left pane lists all email templates with search and filter; right pane provides a full-featured
 * editor and live client-side rendered preview with sample variables.
 */

interface TemplateRow {
  key: EmailTemplateKey;
  label: string;
  trigger: string;
  recipient: string;
  variables: EmailTemplateVar[];
  lockedNote: string;
  defaultSubject: string;
  defaultBody: string;
  subjectOverride: string | null;
  bodyOverride: string | null;
  /** 0085: owner-authored HTML body. Null = automatic paragraph rendering from the plain text. */
  bodyHtmlOverride: string | null;
  resolvedSubject: string;
  resolvedBody: string;
  /** 0085: false means this email is not sent at all. */
  enabled: boolean;
  /** False for sign-in / OTP / reset emails, which have no off switch. */
  canDisable: boolean;
  customized: boolean;
  updatedAt: string | null;
}

interface TemplatesResponse {
  templates: TemplateRow[];
}

const MONO: CSSProperties = {
  whiteSpace: "pre-wrap",
  fontFamily: "var(--mono)",
  fontSize: 12.5,
  lineHeight: 1.6,
  background: "var(--surface-2)",
  borderRadius: 10,
  padding: "12px 14px",
  margin: 0,
  color: "var(--ink)",
  border: "1px solid var(--line-soft)",
};

function sampleVars(variables: EmailTemplateVar[]): Record<string, string> {
  const out: Record<string, string> = {};
  for (const v of variables) out[v.key] = v.sample;
  return out;
}

function TemplateEditor({
  t,
  isOwner,
  ownerEmail,
}: {
  t: TemplateRow;
  isOwner: boolean;
  ownerEmail: string | null;
}): ReactNode {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [subjectDraft, setSubjectDraft] = useState(t.subjectOverride ?? t.resolvedSubject);
  const [bodyDraft, setBodyDraft] = useState(t.bodyOverride ?? t.resolvedBody);
  const [err, setErr] = useState<string | null>(null);
  const [testMsg, setTestMsg] = useState<string | null>(null);
  const [copiedVar, setCopiedVar] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<"edit" | "preview">("edit");
  // 0085 — HTML mode. A template is in HTML mode when an HTML body is stored; switching to it
  // seeds the editor from the plain-text body so an owner starts from their own words.
  const [htmlMode, setHtmlMode] = useState(t.bodyHtmlOverride !== null);
  const [htmlDraft, setHtmlDraft] = useState(t.bodyHtmlOverride ?? "");

  // Sync draft when template changes or is updated from server
  useEffect(() => {
    setEditing(false);
    setSubjectDraft(t.subjectOverride ?? t.resolvedSubject);
    setBodyDraft(t.bodyOverride ?? t.resolvedBody);
    setHtmlMode(t.bodyHtmlOverride !== null);
    setHtmlDraft(t.bodyHtmlOverride ?? "");
    setErr(null);
    setTestMsg(null);
  }, [t.key, t.subjectOverride, t.bodyOverride, t.bodyHtmlOverride, t.resolvedSubject, t.resolvedBody]);

  const save = useMutation({
    mutationFn: (payload: {
      subjectOverride: string | null;
      bodyOverride: string | null;
      bodyHtmlOverride?: string | null;
    }) => api.put(`/admin/email-templates/${t.key}`, payload),
    onSuccess: () => {
      setEditing(false);
      setErr(null);
      void qc.invalidateQueries({ queryKey: ["email-templates"] });
    },
    onError: (e) => setErr(apiErrorText(e, "Couldn't save this template.")),
  });

  // Sends the template AS IT WOULD SEND TODAY (saved override, or default) — not the unsaved
  // draft — through the same outbox → worker → ACS path every real email uses. Recipient is
  // always the caller's own account email, resolved server-side.
  const testSend = useMutation({
    mutationFn: () => api.post<{ to: string; queued: boolean }>(`/admin/email-templates/${t.key}/test`),
    onSuccess: (res) => {
      setErr(null);
      setTestMsg(`Test email queued to ${res.to}. It'll arrive once the mail worker sends it.`);
    },
    onError: (e) => setErr(apiErrorText(e, "Couldn't queue a test email.")),
  });

  /** The send switch. Separate from Save so it takes effect on one click, like every other
   *  toggle in this console — an owner pausing an email should not have to also save wording. */
  const setEnabled = useMutation({
    mutationFn: (enabled: boolean) =>
      api.put(`/admin/email-templates/${t.key}`, {
        // Sent alongside so the row is never left carrying only a flag with no wording, which
        // the table's not-empty constraint would reject on a template that has no override yet.
        subjectOverride: t.subjectOverride,
        bodyOverride: t.bodyOverride,
        enabled,
      }),
    onSuccess: () => {
      setErr(null);
      void qc.invalidateQueries({ queryKey: ["email-templates"] });
    },
    onError: (e) => setErr(apiErrorText(e, "Couldn't change that setting.")),
  });

  const vars = useMemo(() => sampleVars(t.variables), [t.variables]);
  const activeSubject = editing ? subjectDraft : t.resolvedSubject;
  const activeBody = editing ? bodyDraft : t.resolvedBody;
  const previewSubject = renderEmailTokens(activeSubject, vars, false);
  const previewBody = renderEmailTokens(activeBody, vars, false);
  // Which body the preview shows: the draft HTML while editing in HTML mode, otherwise whatever
  // is stored. Variable values are escaped (true) — they stand in for candidate-supplied data.
  const activeHtml = editing ? (htmlMode ? htmlDraft : null) : t.bodyHtmlOverride;
  const previewIsHtml = Boolean(activeHtml && activeHtml.trim());
  const previewHtml = previewIsHtml ? renderEmailTokens(activeHtml!, vars, true) : "";

  const handleCopyVar = (key: string) => {
    const token = `{{${key}}}`;
    void navigator.clipboard.writeText(token);
    setCopiedVar(key);
    setTimeout(() => setCopiedVar(null), 2000);
  };

  const handleInsertVar = (key: string) => {
    const token = `{{${key}}}`;
    if (editing) {
      setBodyDraft((prev) => prev + (prev.endsWith(" ") || prev.endsWith("\n") || !prev ? "" : " ") + token);
    }
    handleCopyVar(key);
  };

  return (
    <div className="card" style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      {/* Detail Header */}
      <div
        className="card-h"
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          gap: 16,
          flexWrap: "wrap",
          padding: "18px 22px",
          borderBottom: "1px solid var(--line)",
        }}
      >
        <div style={{ flex: 1, minWidth: 260 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
            <h2 style={{ fontSize: 18, fontWeight: 600, margin: 0, color: "var(--ink)" }}>{t.label}</h2>
            <span className={t.customized ? "pill t" : "pill n"}>
              {t.customized ? "customized" : "default"}
            </span>
            {/* An email that is switched off is the most important thing on this card — a
                colleague wondering why candidates stopped hearing from us reads this first. */}
            {!t.enabled && <span className="pill r">not being sent</span>}
            {t.bodyHtmlOverride !== null && <span className="pill n">HTML</span>}
          </div>
          <div className="mut sm" style={{ marginTop: 6, display: "flex", gap: 14, flexWrap: "wrap" }}>
            <span>
              <strong>Recipient:</strong> {t.recipient}
            </span>
            <span>•</span>
            <span>
              <strong>Trigger:</strong> {t.trigger}
            </span>
          </div>
        </div>

        {/* Header Action Buttons */}
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          {!editing ? (
            <>
              <button
                className="btn primary sm"
                disabled={!isOwner}
                onClick={() => {
                  setSubjectDraft(t.subjectOverride ?? t.resolvedSubject);
                  setBodyDraft(t.bodyOverride ?? t.resolvedBody);
                  setEditing(true);
                }}
                style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
              >
                <Edit /> {t.customized ? "Edit wording" : "Customize wording"}
              </button>
              {t.customized && (
                <button
                  className="btn ghost sm"
                  disabled={!isOwner || save.isPending}
                  onClick={() => save.mutate({ subjectOverride: null, bodyOverride: null })}
                  title="Revert subject and body overrides back to default"
                  style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
                >
                  <RefreshCw /> Reset to default
                </button>
              )}
              {/* The send switch. Absent entirely on templates that cannot be switched off —
                  offering a disabled control for something that is never permitted invites the
                  question "why won't this work?"; not offering it answers it. */}
              {t.canDisable ? (
                <label
                  style={{ display: "inline-flex", alignItems: "center", gap: 8, cursor: isOwner ? "pointer" : "default" }}
                  title={t.enabled ? "Stop sending this email" : "Start sending this email again"}
                >
                  <Switch
                    on={t.enabled}
                    disabled={!isOwner || setEnabled.isPending}
                    onToggle={() => setEnabled.mutate(!t.enabled)}
                  />
                  <span className="mut sm">{t.enabled ? "Sending" : "Paused"}</span>
                </label>
              ) : (
                <span className="mut sm" title="People rely on this email to sign in or verify their identity">
                  Always on
                </span>
              )}
              <button
                className="btn ghost sm"
                disabled={!isOwner || testSend.isPending}
                onClick={() => {
                  setErr(null);
                  setTestMsg(null);
                  testSend.mutate();
                }}
                title={
                  ownerEmail
                    ? `Send a real test email (with sample data) to ${ownerEmail}`
                    : "Send a real test email (with sample data) to your account"
                }
                style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
              >
                <Mail /> {testSend.isPending ? "Sending…" : "Send test email"}
              </button>
            </>
          ) : (
            <>
              <button
                className="btn ghost sm"
                disabled={save.isPending}
                onClick={() => {
                  setEditing(false);
                  setSubjectDraft(t.subjectOverride ?? t.resolvedSubject);
                  setBodyDraft(t.bodyOverride ?? t.resolvedBody);
                  setErr(null);
                }}
              >
                Cancel
              </button>
              <button
                className="btn primary sm"
                disabled={
                  save.isPending ||
                  subjectDraft.trim().length === 0 ||
                  (htmlMode ? htmlDraft.trim().length === 0 : bodyDraft.trim().length === 0)
                }
                onClick={() =>
                  save.mutate({
                    subjectOverride: subjectDraft.trim(),
                    // The plain-text body is stored EITHER WAY. It is the email's text/plain
                    // part — the version shown in clients that refuse HTML and read aloud by
                    // screen readers — so an HTML template without it would send half an email.
                    bodyOverride: bodyDraft.trim(),
                    // Explicit null when switching back to plain text, so the stored HTML is
                    // cleared rather than silently left behind to win on the next send.
                    bodyHtmlOverride: htmlMode ? htmlDraft.trim() : null,
                  })
                }
                style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
              >
                <Check /> {save.isPending ? "Saving…" : "Save template"}
              </button>
            </>
          )}
        </div>
      </div>

      {/* Detail Content */}
      <div className="card-b" style={{ padding: "20px 22px", flex: 1, display: "flex", flexDirection: "column", gap: 20 }}>
        {!isOwner && (
          <div className="banner" style={{ background: "var(--surface-2)", padding: "10px 14px", borderRadius: 8, display: "flex", gap: 10, alignItems: "center" }}>
            <Info />
            <span className="sm mut">Only users with the Owner role can modify email template wording.</span>
          </div>
        )}

        {testMsg && (
          <div
            className="banner"
            style={{
              background: "var(--good-wash)",
              border: "1px solid var(--good)",
              padding: "10px 14px",
              borderRadius: 8,
              display: "flex",
              gap: 10,
              alignItems: "center",
            }}
          >
            <Check />
            <span className="sm">{testMsg}</span>
          </div>
        )}

        {err && (
          <div className="banner crit" style={{ display: "flex", gap: 10, alignItems: "center" }}>
            <Alert />
            <span>{err}</span>
          </div>
        )}

        {/* Subject Section */}
        <div>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
            <label className="mut sm" style={{ fontWeight: 600 }}>
              Subject line
            </label>
            {editing && (
              <span className="mut sm" style={{ fontSize: 12 }}>
                {subjectDraft.length} chars
              </span>
            )}
          </div>
          {editing ? (
            <input
              className="input"
              value={subjectDraft}
              onChange={(e) => setSubjectDraft(e.target.value)}
              placeholder="Enter email subject line..."
              style={{ width: "100%", fontSize: 13.5, padding: "10px 12px" }}
            />
          ) : (
            <pre style={MONO}>{t.resolvedSubject}</pre>
          )}
        </div>

        {/* Body Section */}
        <div>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
            <label className="mut sm" style={{ fontWeight: 600 }}>
              {htmlMode ? "HTML body" : "Body text"}
            </label>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              {editing && (
                <span className="mut sm" style={{ fontSize: 12 }}>
                  {(htmlMode ? htmlDraft : bodyDraft).length} chars
                </span>
              )}
              {/* Plain text is the DEFAULT and the safe path: it cannot produce broken markup,
                  and most mail clients render the auto-generated paragraphs better than
                  hand-written HTML. HTML is offered for owners who need layout, not as the
                  recommended way to write an email. */}
              {editing && (
                <button
                  type="button"
                  className="btn ghost sm"
                  onClick={() => {
                    setHtmlMode((on) => {
                      // Switching INTO HTML seeds the editor from the plain-text body so the
                      // owner starts from their own words rather than an empty box.
                      if (!on && !htmlDraft.trim()) {
                        setHtmlDraft(
                          bodyDraft
                            .split(/\n{2,}/)
                            .map((p) => `<p>${p.trim().replace(/\n/g, "<br/>")}</p>`)
                            .filter((p) => p !== "<p></p>")
                            .join("\n"),
                        );
                      }
                      return !on;
                    });
                  }}
                >
                  {htmlMode ? "Use plain text" : "Use HTML"}
                </button>
              )}
            </div>
          </div>

          {editing ? (
            htmlMode ? (
              <>
                <textarea
                  className="input"
                  value={htmlDraft}
                  onChange={(e) => setHtmlDraft(e.target.value)}
                  rows={12}
                  placeholder="<p>Hello {{candidate_name}},</p>"
                  style={{ width: "100%", fontFamily: "var(--mono)", fontSize: 12.5, lineHeight: 1.6, padding: "12px" }}
                />
                <div className="mut sm" style={{ marginTop: 6, fontSize: 12, lineHeight: 1.5 }}>
                  Paragraphs, lists, links, tables and basic formatting. Scripts, styles and
                  inline event handlers are rejected on save — they're stripped by mail clients
                  anyway. The button and footer below are still added automatically.
                </div>
              </>
            ) : (
              <textarea
                className="input"
                value={bodyDraft}
                onChange={(e) => setBodyDraft(e.target.value)}
                rows={9}
                placeholder="Enter email body text..."
                style={{ width: "100%", fontFamily: "var(--sans)", fontSize: 13.5, lineHeight: 1.6, padding: "12px" }}
              />
            )
          ) : (
            <pre style={MONO}>{t.bodyHtmlOverride ?? t.resolvedBody}</pre>
          )}
        </div>

        {/* Available Variables */}
        {t.variables.length > 0 && (
          <div
            style={{
              background: "var(--surface-2)",
              border: "1px solid var(--line-soft)",
              borderRadius: 10,
              padding: "14px 16px",
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
              <div style={{ fontSize: 12.5, fontWeight: 600, color: "var(--ink-soft)" }}>
                Available Variables ({t.variables.length})
              </div>
              <span className="mut sm" style={{ fontSize: 12 }}>
                {editing ? "Click to insert into body" : "Click to copy"}
              </span>
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
              {t.variables.map((v) => {
                const isCopied = copiedVar === v.key;
                return (
                  <button
                    key={v.key}
                    type="button"
                    onClick={() => (editing ? handleInsertVar(v.key) : handleCopyVar(v.key))}
                    title={`${v.label} (Sample: "${v.sample}")`}
                    style={{
                      background: isCopied ? "var(--good-wash)" : "var(--surface)",
                      border: `1px solid ${isCopied ? "var(--good)" : "var(--line)"}`,
                      borderRadius: 6,
                      padding: "4px 10px",
                      fontSize: 12,
                      fontFamily: "var(--mono)",
                      color: isCopied ? "var(--good)" : "var(--teal-ink)",
                      cursor: "pointer",
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 6,
                      transition: "all 0.15s ease",
                    }}
                  >
                    <span>{`{{${v.key}}}`}</span>
                    {isCopied ? <Check style={{ width: 12, height: 12 }} /> : <Copy style={{ width: 12, height: 12, opacity: 0.6 }} />}
                  </button>
                );
              })}
            </div>
            <div className="mut sm" style={{ marginTop: 8, fontSize: 12, lineHeight: 1.4 }}>
              Variables are replaced dynamically during send. Values like dates and counts are pre-formatted.
            </div>
          </div>
        )}

        {/* Locked Note Banner */}
        <div
          style={{
            background: "color-mix(in srgb, var(--teal-wash) 40%, var(--surface-2))",
            border: "1px solid var(--line)",
            borderRadius: 10,
            padding: "12px 16px",
            display: "flex",
            gap: 12,
            alignItems: "flex-start",
          }}
        >
          <div style={{ color: "var(--teal)", marginTop: 2 }}>
            <Info />
          </div>
          <div>
            <div style={{ fontSize: 12.5, fontWeight: 600, color: "var(--ink)" }}>System elements (Locked)</div>
            <p className="mut sm" style={{ margin: "3px 0 0", lineHeight: 1.5, fontSize: 12 }}>
              {t.lockedNote}
            </p>
          </div>
        </div>

        {/* Live Preview Section */}
        <div style={{ marginTop: 4 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <Mail />
              <span style={{ fontSize: 13, fontWeight: 600, color: "var(--ink)" }}>
                Live Preview <span className="mut sm" style={{ fontWeight: 400 }}>(with sample data)</span>
              </span>
            </div>
            <span className="pill n" style={{ fontSize: 12 }}>Client-side render</span>
          </div>

          <div
            style={{
              background: "var(--surface)",
              border: "1px solid var(--line)",
              borderRadius: 12,
              overflow: "hidden",
              boxShadow: "var(--shadow-sm)",
            }}
          >
            <div
              style={{
                background: "var(--surface-2)",
                padding: "10px 16px",
                borderBottom: "1px solid var(--line)",
                display: "flex",
                flexDirection: "column",
                gap: 4,
                fontSize: 12.5,
              }}
            >
              <div style={{ display: "flex", gap: 8 }}>
                <span className="mut" style={{ width: 55 }}>To:</span>
                <span style={{ color: "var(--ink)", fontWeight: 500 }}>
                  {vars["name"] ? `${vars["name"]} <sample@example.com>` : `${t.recipient} <sample@example.com>`}
                </span>
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                <span className="mut" style={{ width: 55 }}>Subject:</span>
                <span style={{ color: "var(--ink)", fontWeight: 600 }}>{previewSubject || "—"}</span>
              </div>
            </div>

            <div style={{ padding: "18px 20px" }}>
              {/* HTML previews render as HTML — the point of writing HTML is seeing the layout.
                  Safe to inject here because the same markup is validated before it can be
                  SAVED (validateEmailHtml rejects scripts, event handlers and javascript: URLs),
                  and because the only author is the owner previewing their own draft. Variable
                  values are escaped during substitution either way. */}
              {previewIsHtml ? (
                <div
                  style={{ fontFamily: "var(--sans)", fontSize: 13.5, lineHeight: 1.6, color: "var(--ink)", marginBottom: 16 }}
                  dangerouslySetInnerHTML={{ __html: previewHtml }}
                />
              ) : (
                <div
                  style={{
                    whiteSpace: "pre-wrap",
                    fontFamily: "var(--sans)",
                    fontSize: 13.5,
                    lineHeight: 1.6,
                    color: "var(--ink)",
                    marginBottom: 16,
                  }}
                >
                  {previewBody}
                </div>
              )}

              {/* Sample Locked Action Button / Code Box */}
              {t.key === "login_code" || t.key === "candidate_otp" ? (
                <div style={{ margin: "20px 0 16px" }}>
                  <div
                    style={{
                      display: "inline-block",
                      letterSpacing: "6px",
                      fontFamily: "var(--mono)",
                      fontSize: 22,
                      fontWeight: 700,
                      background: "var(--surface-2)",
                      border: "1px solid var(--line)",
                      borderRadius: 8,
                      padding: "10px 18px",
                      color: "var(--teal-ink)",
                    }}
                  >
                    849 201
                  </div>
                </div>
              ) : t.lockedNote.toLowerCase().includes("button") || t.key.includes("link") || t.key.includes("invite") || t.key.includes("report") || t.key.includes("reset") ? (
                <div style={{ margin: "20px 0 16px" }}>
                  <button
                    type="button"
                    disabled
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 6,
                      background: "var(--solid)",
                      color: "var(--rail-on)",
                      border: "none",
                      borderRadius: 6,
                      padding: "10px 20px",
                      fontWeight: 600,
                      fontSize: 13.5,
                      cursor: "default",
                      opacity: 0.95,
                    }}
                  >
                    {t.key === "interview_link" || t.key === "interview_scheduled"
                      ? "Start interview →"
                      : t.key === "link_recovered"
                      ? "Continue interview →"
                      : t.key === "report_ready"
                      ? "View report →"
                      : t.key === "account_invite"
                      ? "Accept invitation →"
                      : t.key === "reset_password"
                      ? "Reset password →"
                      : "Open link →"}
                  </button>
                </div>
              ) : null}

              {/* Sample Locked Footer representation */}
              <div
                style={{
                  marginTop: 18,
                  paddingTop: 14,
                  borderTop: "1px dashed var(--line)",
                  color: "var(--ink-mute)",
                  fontSize: 12,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <span>[Platform automated link / button & compliance notice appended here]</span>
                <span className="pill n" style={{ fontSize: 12 }}>Auto-generated</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export function EmailTemplates(): ReactNode {
  const { me } = useAuth();
  const isOwner = me?.role === "owner";

  const [selectedKey, setSelectedKey] = useState<EmailTemplateKey | null>(null);
  const [search, setSearch] = useState("");
  const [filterMode, setFilterMode] = useState<"all" | "customized" | "default">("all");

  const { data, isLoading, isError } = useQuery({
    queryKey: ["email-templates"],
    queryFn: () => api.get<TemplatesResponse>("/admin/email-templates"),
  });

  const templates = data?.templates ?? [];

  // Filter templates by search string and filter mode
  const filteredTemplates = useMemo(() => {
    const q = search.trim().toLowerCase();
    return templates.filter((t) => {
      if (filterMode === "customized" && !t.customized) return false;
      if (filterMode === "default" && t.customized) return false;
      if (!q) return true;
      return (
        t.label.toLowerCase().includes(q) ||
        t.trigger.toLowerCase().includes(q) ||
        t.recipient.toLowerCase().includes(q) ||
        t.key.toLowerCase().includes(q)
      );
    });
  }, [templates, search, filterMode]);

  // Ensure an active template is selected
  useEffect(() => {
    if (templates.length > 0 && !selectedKey && templates[0]) {
      setSelectedKey(templates[0].key);
    }
  }, [templates, selectedKey]);

  const selectedTemplate = useMemo(() => {
    return templates.find((t) => t.key === selectedKey) ?? templates[0] ?? null;
  }, [templates, selectedKey]);

  const customizedCount = templates.filter((t) => t.customized).length;

  return (
    <section style={{ maxWidth: 1400, margin: "0 auto" }}>
      {/* Page Header */}
      <div className="page-h" style={{ marginBottom: 20 }}>
        <div>
          <h1>Email templates</h1>
          <div className="sub">
            Every transactional email TalentHop sends. Select a template on the left to customize its subject and body.
          </div>
        </div>
        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
          <span className="pill n">applies within ~1 min</span>
        </div>
      </div>

      {isLoading && <div className="card pad mut sm">Loading email templates…</div>}
      {isError && (
        <div className="banner crit" style={{ marginBottom: 16 }}>
          <span>Couldn't load the email template registry. Please retry.</span>
        </div>
      )}

      {!isLoading && !isError && (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "340px 1fr",
            gap: 20,
            alignItems: "start",
          }}
        >
          {/* LEFT COLUMN: Template Master List */}
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 12,
              position: "sticky",
              top: 20,
              maxHeight: "calc(100vh - 120px)",
            }}
          >
            {/* Search and Filters Card */}
            <div className="card" style={{ padding: "14px 16px" }}>
              <div
                style={{
                  position: "relative",
                  display: "flex",
                  alignItems: "center",
                  marginBottom: 12,
                }}
              >
                <input
                  className="input sm"
                  type="text"
                  placeholder="Search templates..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  style={{
                    width: "100%",
                    paddingLeft: 32,
                    fontSize: 13,
                    height: 36,
                  }}
                />
                <div
                  style={{
                    position: "absolute",
                    left: 10,
                    color: "var(--ink-mute)",
                    display: "flex",
                    alignItems: "center",
                    pointerEvents: "none",
                  }}
                >
                  <Search style={{ width: 15, height: 15 }} />
                </div>
                {search && (
                  <button
                    type="button"
                    onClick={() => setSearch("")}
                    className="btn ghost sm"
                    style={{
                      position: "absolute",
                      right: 6,
                      padding: "2px 6px",
                      fontSize: 12,
                      height: 24,
                    }}
                  >
                    Clear
                  </button>
                )}
              </div>

              {/* Filter Tabs */}
              <div style={{ display: "flex", gap: 6 }}>
                <button
                  type="button"
                  onClick={() => setFilterMode("all")}
                  className={filterMode === "all" ? "btn primary sm" : "btn ghost sm"}
                  style={{ flex: 1, padding: "5px 0", fontSize: 12, justifyContent: "center" }}
                >
                  All ({templates.length})
                </button>
                <button
                  type="button"
                  onClick={() => setFilterMode("customized")}
                  className={filterMode === "customized" ? "btn primary sm" : "btn ghost sm"}
                  style={{ flex: 1, padding: "5px 0", fontSize: 12, justifyContent: "center" }}
                >
                  Custom ({customizedCount})
                </button>
                <button
                  type="button"
                  onClick={() => setFilterMode("default")}
                  className={filterMode === "default" ? "btn primary sm" : "btn ghost sm"}
                  style={{ flex: 1, padding: "5px 0", fontSize: 12, justifyContent: "center" }}
                >
                  Default ({templates.length - customizedCount})
                </button>
              </div>
            </div>

            {/* Scrollable Template List */}
            <div
              className="card"
              style={{
                padding: 6,
                overflowY: "auto",
                maxHeight: "calc(100vh - 240px)",
                display: "flex",
                flexDirection: "column",
                gap: 4,
              }}
            >
              {filteredTemplates.length === 0 ? (
                <div style={{ padding: "24px 16px", textAlign: "center" }} className="mut sm">
                  No templates match your filter.
                </div>
              ) : (
                filteredTemplates.map((t) => {
                  const isSelected = selectedTemplate?.key === t.key;
                  return (
                    <button
                      key={t.key}
                      type="button"
                      onClick={() => setSelectedKey(t.key)}
                      style={{
                        textAlign: "left",
                        padding: "10px 12px",
                        borderRadius: 8,
                        border: isSelected ? "1px solid var(--teal)" : "1px solid transparent",
                        background: isSelected
                          ? "var(--teal-wash)"
                          : "transparent",
                        cursor: "pointer",
                        transition: "all 0.15s ease",
                        display: "flex",
                        flexDirection: "column",
                        gap: 4,
                        position: "relative",
                      }}
                      onMouseEnter={(e) => {
                        if (!isSelected) e.currentTarget.style.background = "var(--surface-2)";
                      }}
                      onMouseLeave={(e) => {
                        if (!isSelected) e.currentTarget.style.background = "transparent";
                      }}
                    >
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 6 }}>
                        <span
                          style={{
                            fontWeight: isSelected ? 600 : 500,
                            fontSize: 13,
                            color: isSelected ? "var(--teal-ink)" : "var(--ink)",
                            lineHeight: 1.3,
                          }}
                        >
                          {t.label}
                        </span>
                        <span
                          className={t.customized ? "pill t" : "pill n"}
                          style={{ fontSize: 12, padding: "2px 6px", flexShrink: 0 }}
                        >
                          {t.customized ? "custom" : "default"}
                        </span>
                      </div>
                      <div
                        className="mut"
                        style={{
                          fontSize: 12,
                          lineHeight: 1.3,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {t.recipient} • {t.trigger}
                      </div>
                    </button>
                  );
                })
              )}
            </div>
          </div>

          {/* RIGHT COLUMN: Selected Template Detail & Editor */}
          <div style={{ minWidth: 0 }}>
            {selectedTemplate ? (
              <TemplateEditor
                key={selectedTemplate.key}
                t={selectedTemplate}
                isOwner={isOwner}
                ownerEmail={me?.email ?? null}
              />
            ) : (
              <div className="card pad mut sm" style={{ textAlign: "center", padding: 40 }}>
                Select a template from the list on the left to view or edit.
              </div>
            )}
          </div>
        </div>
      )}

      <div className="disclaimer" style={{ marginTop: 24 }}>
        A button, code, or compliance list some emails carry is always added by the server after
        this text and can't be edited away — see each template's note. Every save is audited.
      </div>
    </section>
  );
}
